/**
 * Customer Cryptography Service
 *
 * Handles customer-specific cryptographic operations:
 * - Argon2id key derivation from passphrase
 * - X25519 key pair generation from derived seed
 * - Passphrase strength validation
 *
 * Security Architecture:
 * passphrase + salt → Argon2id → 32-byte seed → X25519 key pair
 *
 * The customer's X25519 key pair is deterministically derived from their
 * passphrase, so they can regenerate it without storing the private key.
 */

import { argon2id } from '@noble/hashes/argon2.js'
import { x25519 } from '@noble/curves/ed25519.js'

import type { DerivedKey, CustomerKeyPair, PassphraseStrength } from '@/types/crypto'
import { ARGON2_PARAMS } from '@/types/crypto'
import { base64ToUint8Array, uint8ArrayToBase64 } from './crypto.service'

/**
 * Customer Cryptography Service
 */
class CustomerCryptoService {
  /**
   * Derive a key from passphrase using Argon2id
   *
   * Argon2id is a memory-hard key derivation function that resists:
   * - GPU-based brute force attacks (due to memory requirements)
   * - ASIC attacks (due to memory-hardness)
   * - Side-channel attacks (Argon2id variant)
   *
   * @param passphrase - Customer's passphrase (minimum 12 characters)
   * @param salt - Optional salt (32 bytes). If not provided, generates new one.
   * @returns Derived key and salt
   */
  deriveKeyFromPassphrase(passphrase: string, salt?: Uint8Array): DerivedKey {
    // Generate salt if not provided
    const keySalt = salt || crypto.getRandomValues(new Uint8Array(32))

    // Convert passphrase to Uint8Array
    const passphraseBytes = new TextEncoder().encode(passphrase)

    // Derive key using Argon2id
    // Parameters from ARGON2_PARAMS:
    // - t: 3 iterations (time cost)
    // - m: 65536 KB (64 MB memory cost)
    // - p: 4 threads (parallelism)
    // - dkLen: 32 bytes (256-bit output)
    const derivedKey = argon2id(passphraseBytes, keySalt, ARGON2_PARAMS)

    return {
      key: derivedKey,
      salt: keySalt,
    }
  }

  /**
   * Generate X25519 key pair from derived seed
   *
   * Uses the Argon2id-derived key as a seed for X25519 key pair generation.
   * This allows deterministic key pair regeneration from passphrase.
   *
   * @param seed - 32-byte seed (from Argon2id derivation)
   * @returns X25519 key pair (private key and public key)
   */
  generateKeyPairFromSeed(seed: Uint8Array): CustomerKeyPair {
    if (seed.length !== 32) {
      throw new Error('Seed must be exactly 32 bytes')
    }

    // The seed IS the private key for X25519
    // X25519 private keys are 32 bytes of random data
    const privateKey = seed

    // Derive public key from private key
    const publicKey = x25519.getPublicKey(privateKey)

    return {
      privateKey,
      publicKey,
    }
  }

  /**
   * Generate customer key pair from passphrase
   *
   * Complete flow: passphrase + salt → Argon2id → seed → X25519 key pair
   *
   * @param passphrase - Customer's passphrase
   * @param salt - Salt for key derivation (required for re-derivation)
   * @returns Key pair and salt
   */
  deriveKeyPairFromPassphrase(
    passphrase: string,
    salt?: Uint8Array
  ): { keyPair: CustomerKeyPair; salt: Uint8Array } {
    // Derive key from passphrase
    const { key: seed, salt: usedSalt } = this.deriveKeyFromPassphrase(passphrase, salt)

    // Generate key pair from seed
    const keyPair = this.generateKeyPairFromSeed(seed)

    return {
      keyPair,
      salt: usedSalt,
    }
  }

  /**
   * Get public key as Base64 string
   *
   * @param keyPair - Customer key pair
   * @returns Base64-encoded public key
   */
  getPublicKeyBase64(keyPair: CustomerKeyPair): string {
    return uint8ArrayToBase64(keyPair.publicKey)
  }

  /**
   * Convert Base64 public key to Uint8Array
   *
   * @param publicKeyBase64 - Base64-encoded public key
   * @returns Public key as Uint8Array
   */
  publicKeyFromBase64(publicKeyBase64: string): Uint8Array {
    return base64ToUint8Array(publicKeyBase64)
  }

  /**
   * Validate passphrase strength
   *
   * Requirements:
   * - Minimum 12 characters
   * - At least one uppercase letter
   * - At least one lowercase letter
   * - At least one number
   *
   * Optional bonus:
   * - Special characters increase score
   * - Longer passphrases increase score
   *
   * @param passphrase - Passphrase to validate
   * @returns Validation result with score and feedback
   */
  validatePassphraseStrength(passphrase: string): PassphraseStrength {
    const feedback: string[] = []
    let score = 0

    // Check length
    if (passphrase.length < 12) {
      feedback.push('Passphrase must be at least 12 characters long')
    } else if (passphrase.length >= 12 && passphrase.length < 16) {
      score += 25
      feedback.push('Good length, but longer is better')
    } else if (passphrase.length >= 16 && passphrase.length < 20) {
      score += 35
    } else {
      score += 40
    }

    // Check for uppercase
    if (!/[A-Z]/.test(passphrase)) {
      feedback.push('Add at least one uppercase letter')
    } else {
      score += 15
    }

    // Check for lowercase
    if (!/[a-z]/.test(passphrase)) {
      feedback.push('Add at least one lowercase letter')
    } else {
      score += 15
    }

    // Check for numbers
    if (!/[0-9]/.test(passphrase)) {
      feedback.push('Add at least one number')
    } else {
      score += 15
    }

    // Check for special characters (bonus)
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(passphrase)) {
      score += 15
      feedback.push('Great! Special characters add extra security')
    }

    // Check for common patterns (penalty)
    const commonPatterns = [
      '123456',
      'password',
      'qwerty',
      'abc123',
      '111111',
      '000000',
      'letmein',
      'welcome',
      'admin',
      'login',
    ]
    for (const pattern of commonPatterns) {
      if (passphrase.toLowerCase().includes(pattern)) {
        score = Math.max(0, score - 30)
        feedback.push('Avoid common patterns like "123456" or "password"')
        break
      }
    }

    // Check for repeating characters (penalty)
    if (/(.)\1{3,}/.test(passphrase)) {
      score = Math.max(0, score - 15)
      feedback.push('Avoid repeating characters')
    }

    // Check for keyboard patterns (penalty)
    const keyboardPatterns = [
      'qwerty',
      'asdf',
      'zxcv',
      'qwertz',
      'azerty',
      '1234',
      '4321',
      'abcd',
      'dcba',
    ]
    for (const pattern of keyboardPatterns) {
      if (passphrase.toLowerCase().includes(pattern)) {
        score = Math.max(0, score - 20)
        feedback.push('Avoid keyboard patterns')
        break
      }
    }

    const isValid =
      passphrase.length >= 12 &&
      /[A-Z]/.test(passphrase) &&
      /[a-z]/.test(passphrase) &&
      /[0-9]/.test(passphrase)

    if (isValid && feedback.length === 0) {
      feedback.push('Strong passphrase!')
    }

    return {
      isValid,
      score: Math.min(100, score),
      feedback,
    }
  }

  /**
   * Generate a random salt (for new registrations)
   *
   * @returns 32-byte random salt
   */
  generateSalt(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(32))
  }

  /**
   * Securely clear sensitive data from memory
   *
   * Note: This provides best-effort clearing but JavaScript
   * doesn't guarantee memory erasure due to garbage collection.
   *
   * @param data - Uint8Array to clear
   */
  secureClear(data: Uint8Array): void {
    crypto.getRandomValues(data) // Overwrite with random data first
    data.fill(0) // Then fill with zeros
  }
}

// Export singleton instance
export const customerCryptoService = new CustomerCryptoService()
